
import { Component, Emit, Prop, Vue, Watch, Mixins, Ref } from 'vue-property-decorator'
import { Observer } from 'mobx-vue'
import Downtime from './Downtime.vue'
import { getLiveStatus, EnumLiveStatus } from '@xt/client/utils/classhour'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiCoupon } from '@xt/client/api/coupon'
import { getLiveLink, GoLiveError, ShouldUseCompanionCardError, isCompanionCardCanUse } from '@xt/client/utils/classhour/live'
import PlaybackExtensionPopup from '@xt/www/components/coupon/PlaybackExtensionPopup.vue'
import type { ClasshourDetail as TClasshourDetail } from '@xt/client/entities/course-v2/types.d'
import type { BaseCouponItem } from '@xt/client/types/coupon'

@Observer
@Component({
  components: { Downtime, PlaybackExtensionPopup }
})
export default class ClasshourLiveStatus extends Vue {
  @Prop() data: TClasshourDetail['classhourInfo']

  key = 0

  date: Record<'day' | 'hour' | 'minute' | 'seconds', number> = { day: 0, hour: 0, minute: 0, seconds: 0 }

  timer: null | ReturnType<typeof setInterval> = null

  showCompanionCardPop = false

  companionCardList: BaseCouponItem[] = []

  get GuideData() {
    return this.$store.$courseV2.GuideData
  }

  get EnumLiveStatus() {
    return EnumLiveStatus
  }

  formatTime(time: number | null) {
    return this.moment(time).format('YYYY.MM.DD HH:mm')
  }

  diffDate(target: number, accuracyUnit: 'days' | 'hours') {
    const duration = this.moment.duration(this.moment(target).diff(this.$ajax.serviceDate()))

    const days = accuracyUnit === 'days' ? Math.floor(duration.asDays()) : 0
    const hours = accuracyUnit === 'days' ? duration.hours() : Math.floor(duration.asHours())
    const minutes = duration.minutes()
    const seconds = duration.seconds()

    return {
      diff: this.moment(target).diff(this.$ajax.serviceDate(), 'millisecond'),
      days,
      hours,
      minutes,
      seconds
    }
  }

  startTick() {
    if (this.$store.$courseV2.GuideData.isGuide) {
      return
    }

    if (this.timer) {
      this.stopTick()
    }

    this.timer = setInterval(() => {
      if (!this.uiStatus) return

      if (this.uiStatus.status === EnumLiveStatus.PlaybackGenerate) {
        const { days, hours, minutes, seconds } = this.diffDate(this.uiStatus.endTime!, 'days')
        this.date = { day: days, hour: hours, minute: minutes, seconds: seconds }
      } else if (this.uiStatus.status === EnumLiveStatus.NotStart) {
        const { days, hours, minutes, seconds } = this.diffDate(this.uiStatus.endTime!, 'days')
        this.date = { day: days, hour: hours, minute: minutes, seconds: seconds }
      } else if (this.uiStatus.status === EnumLiveStatus.StudyReview) {
        const { days, hours, minutes, seconds } = this.diffDate(this.uiStatus.endTime!, 'days')
        this.date = { day: days, hour: hours, minute: minutes, seconds: seconds }
      }

      // 检查一下时间是否已经用尽
      const { day, hour, minute, seconds } = this.date

      if (day === 0 && hour === 0 && minute === 0 && seconds === 0) {
        console.warn('时间用尽')
        this.stopTick()
        this.onRefreshData()
        this.key += 1
      }
    }, 1000)
  }

  stopTick() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
      console.warn('清除了定时器')
    }
  }

  @Watch('data', { immediate: true, deep: true })
  async handleDataChange(data) {
    const status = getLiveStatus(data)
    this.uiStatus = status

    if (this.uiStatus.status === EnumLiveStatus.PlaybackGenerate) {
      const { days, hours, minutes, seconds } = this.diffDate(this.uiStatus.endTime!, 'days')
      this.date = { day: days, hour: hours, minute: minutes, seconds: seconds }
      this.startTick()
    } else if (this.uiStatus.status === EnumLiveStatus.NotStart) {
      const { days, hours, minutes, seconds } = this.diffDate(this.uiStatus.endTime!, 'days')
      this.date = { day: days, hour: hours, minute: minutes, seconds: seconds }
      this.startTick()
    } else if (this.uiStatus.status === EnumLiveStatus.StudyReview) {
      const { days, hours, minutes, seconds } = this.diffDate(this.uiStatus.endTime!, 'days')
      this.date = { day: days, hour: hours, minute: minutes, seconds: seconds }
      this.startTick()
    } else if (this.uiStatus.status === EnumLiveStatus.PlaybackEnd) {
      // 请求当前同学学习回顾卡的数据
      const [error, coupons] = await $service<Array<BaseCouponItem>>({
        method: 'GET',
        url: EnumApiCoupon.CouponPacks,
        params: {
          couponType: 301,
          status: 1
        }
      })

      if (coupons) {
        this.companionCardList = coupons.filter(i => isCompanionCardCanUse(this.data.courseId, i))
      }
    }
    console.log('data 变化', JSON.stringify(status, undefined, 2))
  }

  uiStatus: ReturnType<typeof getLiveStatus> | null = null

  get startTime() {
    return this.data.startTime
  }

  onStudyReview() {
    if (!this.companionCardList.length) {
      this.$msg('没有可用的学习回顾卡，刷新试试～')
      return
    }
    this.showCompanionCardPop = true
  }

  get preventGotoLive() {
    return this.$ajax.serviceDate().isBefore(this.moment(this.startTime).startOf('days'))
  }

  onJumpPlayback() {
    this.hideCompanionCardPop()
    window.open(`/playback?classhour=${this.data.id}`, '_blank')
  }

  hideCompanionCardPop() {
    this.showCompanionCardPop = false
  }

  onRefreshData() {
    this.$emit('refresh')
  }

  async onGolive(isPlayback: boolean) {
    try {
      const { type, url } = await getLiveLink(this.data.id, isPlayback ? 'playback' : 'live', this.data)

      if (type === 'web') {
        window.open(url, '_blank')
      }
    } catch (e) {
      if (e instanceof GoLiveError) {
        this.$message.error(e.message)
      } else if (e instanceof ShouldUseCompanionCardError) {
        this.companionCardList = e.companionCardList
      }
    }
  }

  created() {}
  mounted() {}
  updated() {}
  destroyed() {
    this.stopTick()
  }
}
